import { BaseTexture, Loader, Renderer, Spritesheet, Texture, utils } from "pixi.js"
import { atlasImageDestination, validateAtlas } from "game-common/content/atlas"
import { contentRevision } from "game-common/content/catalog"
import { InlineContentFiles, StagedContentResources } from "game-common/content/publishing"
import { ContentSnapshot } from "game-common/content/store"
import { ContentAssets, ContentAssetGeneration } from "../../../content_assets"
import { resolveConfig } from "../../../runtime_config"
import { getRendererApp } from "./renderer_app"

export interface ContentSheetResource {
    texture?: Texture
    textures?: Record<string, Texture>
    spritesheet?: { animations: Record<string, Texture[]> }
}

let namespaceSequence = 0
let generationSequence = 0

/** A presentation sheet as the startup loader holds it, if it finished loading. */
const loadedAtStartup = (id: string): ContentSheetResource | undefined => {
    const resource = Loader.shared.resources[id]
    return resource?.textures ? resource : undefined
}

/** Private atlas textures; neither preparation nor activation overwrites PIXI's logical caches.
 * Release retirement is explicit: old sprites and baked composites may still own these textures.
 * Only the atlases that changed are built; the rest are ADOPTED from the generation before (or,
 * for media unchanged since startup, left to the startup loaders, which hold identical bytes). */
export class ContentTextureGeneration implements StagedContentResources {
    readonly generation = ++generationSequence
    readonly resources = new Map<string, ContentSheetResource>()
    readonly textures = new Map<string, Texture>()
    private sheets: Spritesheet[] = []
    private bases: BaseTexture[] = []
    private disposed = false
    private published = false
    // Newer generations that adopted these textures, and so may still draw them. A discarded
    // candidate lets go; a retired one does not, because its own heir copied the same references.
    private heirs = 0
    private adoptedPrevious = false
    constructor(readonly assets: ContentAssetGeneration, private owner: ContentTextures, private previous?: ContentTextureGeneration) {}

    async build(snapshot: ContentSnapshot, signal: AbortSignal, upload: (base: BaseTexture) => void) {
        if (this.previous) {
            this.previous.heirs++
            this.adoptedPrevious = true
            this.previous.resources.forEach((resource, key) => this.resources.set(key, resource))
        }
        const resources = new Map<string, ContentSheetResource>()
        for (const [file, document] of this.assets.documents) {
            if (signal.aborted) throw new Error("Content atlas staging aborted")
            const data = document as any
            if (!data?.frames) continue
            const imageFile = atlasImageDestination(file, data.meta?.image)
            const image = imageFile && this.assets.images.get(imageFile)
            if (!image) throw new Error(`Atlas ${file} names an unavailable image`)
            // New base per atlas: PIXI mutates its resolution based on atlas meta.scale.
            const base = new BaseTexture(image.image as any)
            this.bases.push(base)
            if (!base.valid) throw new Error(`Atlas image is not decoded: ${imageFile}`)
            // Legacy atlases include compiler-pinned trims outside their source rectangle.
            // These are legal PIXI textures; runtime still validates image/frame bounds.
            const problems = validateAtlas(data, { width: base.realWidth, height: base.realHeight }, { allowTrimOutsideSource: true })
            if (problems.length) throw new Error(`Invalid atlas ${file}: ${problems.join("; ")}`)
            if (data.meta.scale !== undefined) {
                const scale = data.meta.scale
                const parsed = parseFloat(String(scale))
                if (!["string", "number"].includes(typeof scale) || parsed !== Number(scale) || !(parsed > 0) || !Number.isFinite(parsed) || !Number.isFinite(base.realWidth / parsed) || !Number.isFinite(base.realHeight / parsed))
                    throw new Error(`Invalid atlas scale: ${file}`)
            }
            if (data.meta.related_multi_packs?.length) throw new Error(`Multi-pack atlas staging is unsupported: ${file}`)
            const names = Object.keys(data.frames)
            let prefix: string
            do { prefix = `content-stage-${++namespaceSequence}:` }
            while (names.some(name => Object.prototype.hasOwnProperty.call(utils.TextureCache, prefix + name)))
            const frames = Object.fromEntries(names.map(name => [prefix + name, data.frames[name]]))
            const animationNames = Object.keys(data.animations || {})
            const animations = Object.fromEntries(Object.entries(data.animations || {}).map(([name, entries]) => [prefix + name, (entries as string[]).map(frame => prefix + frame)]))
            const sheet = new Spritesheet(base, { ...data, frames, animations })
            this.sheets.push(sheet)
            try {
                await new Promise<void>(resolve => sheet.parse(() => resolve()))
            } finally {
                // Spritesheet.parse writes global entries, so give it unique private names and
                // remove them after parsing. Never let late loader jobs overwrite staged frames.
                names.forEach(name => Texture.removeFromCache(prefix + name))
            }
            if (signal.aborted) throw new Error("Content atlas staging aborted")
            upload(base)
            const resource: ContentSheetResource = {
                textures: Object.fromEntries(names.map(name => [name, sheet.textures[prefix + name]])),
                spritesheet: { animations: Object.fromEntries(animationNames.map(name => [name, sheet.animations[prefix + name]])) },
            }
            resources.set(file, resource)
            this.resources.set(`assets/${file}`, resource)
            this.resources.set(file, resource)
        }
        for (const { id, file } of snapshot.catalog.source.presentation.sheets) {
            const resource = resources.get(file) || (this.previous ? this.previous.resources.get(file) : loadedAtStartup(id))
            if (!resource) throw new Error(`Missing presentation atlas: ${file}`)
            this.resources.set(id, resource)
            for (const [name, texture] of Object.entries(resource.textures)) this.textures.set(name, texture)
        }
        for (const file of snapshot.catalog.source.preload) {
            if (!resources.has(file) && !this.assets.inherited.has(file)) throw new Error(`Missing preload atlas: ${file}`)
        }
    }

    activate(): () => void {
        if (this.disposed || this.published) throw new Error("Content texture generation is unavailable")
        const previous = this.owner.active
        const undoAssets = this.assets.activate()
        this.owner.active = this
        this.published = true
        return () => {
            if (!this.published || this.owner.active !== this || previous?.disposed) throw new Error("Content texture rollback is stale")
            undoAssets()
            this.owner.active = previous
            this.published = false
        }
    }

    discard(): void {
        if (this.published) throw new Error("Published content textures must be retired by the renderer")
        if (this.adoptedPrevious) { this.adoptedPrevious = false; this.previous.heirs-- }
        this.dispose()
        this.assets.discard()
    }

    releaseRetired(): void {
        if (this.owner.active === this) throw new Error("Cannot retire active content textures")
        if (this.heirs) throw new Error("Retired content textures are still adopted by a newer generation")
        this.dispose()
        this.assets.releaseRetired()
    }

    private dispose() {
        if (this.disposed) return
        this.disposed = true
        this.sheets.forEach(sheet => sheet.destroy(false))
        this.bases.forEach(base => base.destroy())
        this.sheets = []
        this.bases = []
        this.resources.clear()
        this.textures.clear()
    }
}

export class ContentTextures {
    active?: ContentTextureGeneration
    constructor(
        private assets = new ContentAssets(undefined, resolveConfig().assetBase, undefined, () => contentRevision),
        private upload: (base: BaseTexture) => void = uploadContentBase,
    ) {}

    stage = async (snapshot: ContentSnapshot, signal: AbortSignal, inline?: InlineContentFiles): Promise<ContentTextureGeneration> => {
        const previous = this.active
        // Presentation sheets feed the texture map built here, so one can only be inherited when a
        // loaded copy exists to adopt. Everything else falls back to the lazy loaders.
        const sheets = new Map(snapshot.catalog.source.presentation.sheets.map(({ id, file }) => [file, id]))
        const canReuse = (file: string) => !sheets.has(file) || !!(previous ? previous.resources.get(file) : loadedAtStartup(sheets.get(file)))
        const assets = await this.assets.stage(snapshot, signal, canReuse, inline)
        const generation = new ContentTextureGeneration(assets, this, previous)
        try { await generation.build(snapshot, signal, this.upload); return generation }
        catch (error) { generation.discard(); throw error }
    }
}

const uploadContentBase = (base: BaseTexture) => {
    const renderer = getRendererApp().renderer as Renderer
    if (!renderer.texture || renderer.gl.isContextLost()) throw new Error("Content texture staging needs a live WebGL renderer")
    const gl = renderer.gl
    const maximum = gl.getParameter(gl.MAX_TEXTURE_SIZE)
    if (base.realWidth > maximum || base.realHeight > maximum) throw new Error("Content image exceeds this GPU's texture limit")
    renderer.texture.bind(base)
    if (gl.getError() !== gl.NO_ERROR) throw new Error("Content texture upload failed")
}

export const contentTextures = new ContentTextures()
export const contentTextureGeneration = (): number => contentTextures.active?.generation || 0

/** Art a live publication changed comes from the active generation. Anything it did not stage is
 * byte-identical to what the startup loader fetched (live releases cannot remove media). */
export const contentSheetResource = (id: string): ContentSheetResource | undefined =>
    contentTextures.active?.resources.get(id) || Loader.shared.resources[id]

export const contentTexture = (id: string): Texture => {
    return contentTextures.active ? contentTextures.active.textures.get(id) || Texture.EMPTY : Texture.from(id)
}
